import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { useToast } from '@/components/ToastContainer'
import { StatCard, Input, Select, Button, Table, Pagination, Alert, Drawer, Modal, Spinner, DateRangePicker, type TableColumn } from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import { formatDate, formatCurrency, formatNumber, formatCredits } from '@/ui'
import { resolveRange, SHARED_RANGE_PRESETS } from '@/lib/dateRange'
import { RefreshCw } from 'lucide-react'
import styles from './RevenueAnalytics.module.css'

interface SyncResult {
  registered: number
  skipped: number
  refunded: number
  unmapped: number
}

interface RevenuePayment {
  id: string
  user: { id: string | null; email: string | null; name: string | null }
  type: string
  method: string
  channel: string
  buyerName: string | null
  amount: number
  refundedAmount: number
  netAmount: number
  creditsGranted: number | null
  status: string
  orderId: string | null
  smartstoreOrderId: string | null
  receiptUrl: string | null
  commission: number | null
  settledAmount: number | null
  settleExpectedDate: string | null
  settleCompletedDate: string | null
  createdAt: string
}

interface RevenueResponse {
  payments: RevenuePayment[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  summary: {
    grossAmount: number
    refundedAmount: number
    netAmount: number
    count: number
    // 조회 범위 내 완전환불(REFUNDED) 건수 — 결제 건수 병기용
    refundedCount: number
    totalCommission: number
    totalSettledAmount: number
  }
}

// payment_type enum 라벨 (schema.ts paymentTypeEnum)
const TYPE_LABELS: Record<string, string> = {
  PREMIUM_UPGRADE: '프리미엄',
  AI_CREDITS: 'AI 크레딧',
  AI_CREDITS_BUNDLE: '크레딧 번들',
  INVITATION_PACKAGE: '청첩장 패키지',
  CREDITS_10: '크레딧 10',
  CREDITS_20: '크레딧 20',
  CREDITS_30: '크레딧 30',
  CREDITS_50: '크레딧 50',
  CREDITS_100: '크레딧 100',
  INVITATION_MAIN: '청첩장',
  STUDIO_PHOTOS_10: 'AI 스튜디오 +10',
  STUDIO_PHOTOS_20: 'AI 스튜디오 +20',
  STUDIO_PHOTOS_30: 'AI 스튜디오 +30',
  SNAP_MAIN: 'AI 스냅',
  SNAP_PHOTOS_10: 'AI 스냅 +10',
  SNAP_PHOTOS_20: 'AI 스냅 +20',
  THANKYOU_ADDON: '감사장 추가',
  ALL_PACKAGE: '올인원 패키지',
  THANKS_PACKAGE: '감사장 패키지',
}

const CHANNEL_LABELS: Record<string, string> = {
  SITE: '사이트(토스)',
  SMARTSTORE: '스마트스토어',
}

// 상태 Select 전용 센티넬 — 실제 status 값이 아니라 API의 excludeRefunded=true 로 번역된다.
const EXCLUDE_REFUNDED = 'EXCLUDE_REFUNDED'

// 빠른검색 프리셋 — admin 공용(SHARED_RANGE_PRESETS). 기간 해석은 공용 resolveRange(기본 오늘).

// 'YYYY-MM-DD' → 'M.D' (정산일 컬럼 표기)
function fmtMd(ymd: string): string {
  const [, m, d] = ymd.split('-')
  return `${Number(m)}.${Number(d)}`
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/

export function RevenueAnalytics() {
  const { filters, setFilters } = useAdminFilters()
  const navigate = useNavigate()
  const [sp, setSp] = useSearchParams()
  const [selected, setSelected] = useState<RevenuePayment | null>(null)

  // 적용된 기간 — URL 기준(커스텀 from/to 또는 preset). 기본 오늘.
  const { from, to, preset } = resolveRange(sp, 'today')
  const channelFilter = sp.get('channel') ?? ''
  const statusFilter = sp.get('status') ?? ''
  const searchFilter = sp.get('search') ?? ''

  // 검색어 입력값 — URL(search)과 분리해 debounce로 커밋. 주문번호/이름/이메일/구매자명 부분일치.
  const [searchInput, setSearchInput] = useState(searchFilter)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(searchDebounceRef.current), [])

  // 필터(채널/상태/기간/preset)는 유지하고 일부만 갱신하는 헬퍼 — page는 1로 초기화
  // 함수형 업데이터 필수 — debounce 타이머가 낡은 sp로 발사돼 다른 필터를 되돌리는 것 방지.
  // replace=true는 검색어처럼 연속 커밋되는 값에 사용(히스토리 누적 방지).
  const updateParams = (patch: Record<string, string | null>, opts?: { replace?: boolean }) => {
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '') next.delete(k)
          else next.set(k, v)
        }
        return next
      },
      { replace: opts?.replace ?? false },
    )
    setFilters({ page: 1 })
  }

  // 검색어 — 300ms debounce 후 URL에 커밋
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchInput(val)
    clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      updateParams({ search: val.trim() || null }, { replace: true })
    }, 300)
  }

  // 기간 선택 — 프리셋이면 preset 키, 커스텀 범위면 from/to 를 URL 에 커밋.
  const handleRangeChange = (
    range: { from: string | null; to: string | null },
    meta: { presetKey: string | null },
  ) => {
    if (meta.presetKey) {
      updateParams({ preset: meta.presetKey, from: null, to: null })
    } else if (range.from && range.to) {
      updateParams({ from: range.from, to: range.to, preset: null })
    }
  }

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  if (channelFilter) params.set('channel', channelFilter)
  // 상태 Select 하나가 실제 status 값과 UI 전용 '환불 제외'를 겸한다 → 서로 다른 쿼리 파라미터로 번역
  if (statusFilter === EXCLUDE_REFUNDED) params.set('excludeRefunded', 'true')
  else if (statusFilter) params.set('status', statusFilter)
  if (searchFilter) params.set('search', searchFilter)
  params.set('from', from)
  params.set('to', to)
  const fetchUrl = `/api/admin/revenue/payments?${params.toString()}`

  const { data, isLoading, error, refetch } = useApiFetch<RevenueResponse>(fetchUrl)

  // 정산 총액 — 별도 정산 테이블(정산예정일 기준)의 정산금액 합. 매출(주문 기준)과
  // 기간 기준이 다르지만, 같은 조회 기간으로 정산금액 합계를 참고용으로 보여준다.
  const { data: settleData } = useApiFetch<{ summary: { settleAmount: number } }>(
    `/api/admin/smartstore/settlements?from=${from}&to=${to}`,
  )

  // 검색 버튼 로딩 피드백 — useApiFetch는 기존 데이터가 있으면 isLoading=false(SWR)라
  // refetch 중을 못 잡는다. 요청 URL이 바뀌면 응답이 도착할 때까지 searching=true.
  const [searching, setSearching] = useState(false)
  const loadedUrlRef = useRef(fetchUrl)
  useEffect(() => {
    if (loadedUrlRef.current !== fetchUrl) setSearching(true)
  }, [fetchUrl])
  useEffect(() => {
    // data/error가 갱신되면(=fetch 정착) 현재 URL을 로드 완료로 표시
    loadedUrlRef.current = fetchUrl
    setSearching(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error])

  // 스마트스토어 매출 기간 동기화 (백필) — 날짜 범위 모달
  const { addToast } = useToast()
  const [syncOpen, setSyncOpen] = useState(false)
  const [syncFrom, setSyncFrom] = useState('')
  const [syncTo, setSyncTo] = useState('')
  const { trigger: triggerSync, isLoading: syncing } = useApiMutation<SyncResult, { from: string; to: string }>(
    '/api/admin/smartstore/sync',
    'POST',
  )

  const syncRangeValid = YMD_RE.test(syncFrom) && YMD_RE.test(syncTo) && syncFrom <= syncTo

  // 모달 열 때 현재 조회 기간을 기본값으로
  const openSyncModal = () => {
    setSyncFrom(from)
    setSyncTo(to)
    setSyncOpen(true)
  }

  const handleSync = async () => {
    if (!syncRangeValid) return
    const result = await triggerSync({ from: syncFrom, to: syncTo })
    if (result) {
      addToast({
        type: 'success',
        message: `동기화 완료 — 등록 ${result.registered} · 기존 ${result.skipped} · 환불 ${result.refunded}${result.unmapped > 0 ? ` · 매핑누락 ${result.unmapped}` : ''}`,
      })
      setSyncOpen(false)
      refetch()
    } else {
      addToast({ type: 'error', message: '스마트스토어 동기화에 실패했습니다' })
    }
  }

  const columns: TableColumn<RevenuePayment>[] = [
    { key: 'createdAt', label: '일시', render: (row) => formatDate(row.createdAt, 'yyyy.M.d HH:mm'), width: '130px' },
    { key: 'channel', label: '채널', render: (row) => CHANNEL_LABELS[row.channel] ?? row.channel, width: '110px' },
    { key: 'type', label: '상품', render: (row) => <StatusBadge status={row.type} label={TYPE_LABELS[row.type] ?? row.type} />, width: '120px' },
    { key: 'user', label: '유저', render: (row) => row.user.name ?? row.user.email ?? '—' },
    { key: 'buyerName', label: '구매자', render: (row) => row.buyerName ?? '-', width: '90px' },
    { key: 'amount', label: '결제액', render: (row) => formatCurrency(row.amount), align: 'right' as const, width: '110px' },
    { key: 'refundedAmount', label: '환불액', render: (row) => row.refundedAmount > 0 ? formatCurrency(row.refundedAmount) : '-', align: 'right' as const, width: '100px' },
    { key: 'commission', label: '수수료', render: (row) => row.commission != null ? formatCurrency(row.commission) : '-', align: 'right' as const, width: '100px' },
    { key: 'settledAmount', label: '정산예정', render: (row) => row.settledAmount != null ? formatCurrency(row.settledAmount) : '-', align: 'right' as const, width: '110px' },
    { key: 'settleDate', label: '정산일', render: (row) =>
        row.settleCompletedDate ? `${fmtMd(row.settleCompletedDate)} ✓`
        : row.settleExpectedDate ? `${fmtMd(row.settleExpectedDate)} 예정`
        : '-',
      align: 'right' as const, width: '90px' },
    { key: 'netAmount', label: '순매출', render: (row) => formatCurrency(row.netAmount), align: 'right' as const, width: '110px' },
    { key: 'status', label: '상태', render: (row) => <StatusBadge status={row.status} />, width: '100px' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.syncHint}>지정한 기간의 스마트스토어 매출을 가져옵니다</span>
        <Button size="sm" variant="secondary" onClick={openSyncModal}>
          <RefreshCw size={14} />
          기간 동기화
        </Button>
      </div>

      <div className={styles.statGrid}>
        <StatCard label="총 거래액" value={formatCurrency(data?.summary.grossAmount ?? 0)} />
        <StatCard label="환불액" value={formatCurrency(data?.summary.refundedAmount ?? 0)} />
        <StatCard label="순매출" value={formatCurrency(data?.summary.netAmount ?? 0)} />
        <StatCard label="수수료(스토어)" value={formatCurrency(data?.summary.totalCommission ?? 0)} />
        <StatCard label="정산총액(스토어)" value={formatCurrency(settleData?.summary.settleAmount ?? 0)} />
        <StatCard
          label="결제 건수"
          value={`${formatNumber(data?.summary.count ?? 0)}건`}
          // 상태 필터가 걸려 있으면 건수가 이미 그 상태로 좁혀져 병기가 중복/혼란 → 숨긴다
          sub={
            !statusFilter && (data?.summary.refundedCount ?? 0) > 0
              ? `환불 ${formatNumber(data!.summary.refundedCount)}건 포함`
              : undefined
          }
        />
      </div>

      <div className={styles.filters}>
        {/* 주문번호/유저/구매자명 검색 — 300ms debounce 후 URL 커밋 */}
        <Input
          size="sm"
          placeholder="주문번호 / 이메일 / 이름 / 구매자명 검색"
          value={searchInput}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />

        {/* 빠른 검색 + 커스텀 기간 (범위 피커 내장) */}
        <div className={styles.customRange}>
          <DateRangePicker
            size="sm"
            value={{ from, to }}
            activePresetKey={preset}
            presets={SHARED_RANGE_PRESETS}
            onChange={handleRangeChange}
          />
          {searching && <Spinner size="sm" />}
        </div>

        {/* 채널/상태 필터 — 즉시 적용 */}
        <Select
          size="sm"
          value={channelFilter}
          onChange={(e) => updateParams({ channel: e.target.value })}
          options={[
            { value: '', label: '전체 채널' },
            { value: 'SITE', label: '사이트(토스)' },
            { value: 'SMARTSTORE', label: '스마트스토어' },
          ]}
        />
        <Select
          size="sm"
          value={statusFilter}
          onChange={(e) => updateParams({ status: e.target.value })}
          options={[
            { value: '', label: '전체 포함' },
            { value: EXCLUDE_REFUNDED, label: '환불 제외' },
            { value: 'COMPLETED', label: 'COMPLETED' },
            { value: 'PARTIALLY_REFUNDED', label: 'PARTIALLY_REFUNDED' },
            { value: 'REFUNDED', label: 'REFUNDED' },
          ]}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<RevenuePayment>
        columns={columns}
        data={data?.payments ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        onRowClick={(row) => setSelected(row)}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page) => setFilters({ page })}
          size="sm"
        />
      )}

      <Drawer open={!!selected} onClose={() => setSelected(null)} title="매출 상세" size="md">
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-4)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-3)' }}>
              {[
                ['결제 ID', selected.id],
                ['채널', CHANNEL_LABELS[selected.channel] ?? selected.channel],
                ['결제수단', selected.method],
                ['유저', selected.user.name ?? selected.user.email ?? '— (미활성화)'],
                ['구매자(스토어)', selected.buyerName ?? '-'],
                ['상품', TYPE_LABELS[selected.type] ?? selected.type],
                ['결제액', formatCurrency(selected.amount)],
                ['환불액', formatCurrency(selected.refundedAmount)],
                ['수수료', selected.commission != null ? formatCurrency(selected.commission) : '-'],
                ['정산예정금액', selected.settledAmount != null ? formatCurrency(selected.settledAmount) : '-'],
                ['정산예정일', selected.settleExpectedDate ?? '-'],
                ['정산완료일', selected.settleCompletedDate ?? '-'],
                ['순매출', formatCurrency(selected.netAmount)],
                ['크레딧', selected.creditsGranted != null ? formatCredits(selected.creditsGranted) : '-'],
                ['주문 ID', selected.orderId ?? '-'],
                ['스마트스토어 주문번호', selected.smartstoreOrderId ?? '-'],
                ['일시', formatDate(selected.createdAt, 'yyyy년 M월 d일 HH:mm:ss')],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--ds-space-2) 0', borderBottom: '1px solid var(--ds-color-border)' }}>
                  <span style={{ fontSize: 'var(--ds-font-size-sm)', color: 'var(--ds-color-text-tertiary)' }}>{label}</span>
                  <span style={{ fontSize: 'var(--ds-font-size-sm)', fontWeight: 500 }}>{value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--ds-space-2) 0' }}>
                <span style={{ fontSize: 'var(--ds-font-size-sm)', color: 'var(--ds-color-text-tertiary)' }}>상태</span>
                <StatusBadge status={selected.status} />
              </div>
            </div>

            {/* 유저 거래내역 딥링크 — 미활성화 매출(userId=null)은 연결할 회원이 없어 노출하지 않는다 */}
            {selected.user.id && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/ops/users?historyUserId=${selected.user.id}`)}
              >
                이 유저 거래내역 보기 →
              </Button>
            )}
          </div>
        )}
      </Drawer>

      <Modal open={syncOpen} onClose={() => setSyncOpen(false)} title="기간 매출 동기화" size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-4)' }}>
          <p style={{ fontSize: 'var(--ds-font-size-sm)', color: 'var(--ds-color-text-secondary)', margin: 0 }}>
            지정한 기간의 스마트스토어 주문을 조회해 매출을 등록합니다. 이미 등록된 주문은
            건너뛰므로 같은 기간을 여러 번 실행해도 안전합니다.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ds-space-2)' }}>
            <DateRangePicker
              size="sm"
              inline
              presets={[]}
              value={{ from: syncFrom || null, to: syncTo || null }}
              onChange={(range) => {
                setSyncFrom(range.from ?? '')
                setSyncTo(range.to ?? '')
              }}
              placeholder="기간 선택"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--ds-space-2)' }}>
            <Button size="sm" variant="ghost" onClick={() => setSyncOpen(false)} disabled={syncing}>
              취소
            </Button>
            <Button size="sm" variant="primary" onClick={handleSync} disabled={!syncRangeValid || syncing}>
              <RefreshCw size={14} className={syncing ? styles.spinning : undefined} />
              {syncing ? '동기화 중…' : '동기화'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
